import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { listInsightsAdmin, deleteInsight } from "@/lib/api/insights.functions";
import { DataTable } from "@/components/admin/DataTable";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { InsightsAdminNav } from "@/components/admin/InsightsAdminNav";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { tableActionsColumn, TableRowActions } from "@/components/admin/TableRowActions";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/insights/")({
  loader: () => listInsightsAdmin({ data: {} }),
  component: AdminInsightsList,
});

type InsightRow = Awaited<ReturnType<typeof listInsightsAdmin>>[number];

function buildColumns(onDelete: (row: InsightRow) => Promise<void>): ColumnDef<InsightRow>[] {
  return [
  {
    accessorKey: "title",
    header: "Title",
    cell: ({ row }) => <span className="font-medium">{row.original.title}</span>,
  },
  { accessorKey: "slug", header: "Slug", meta: { filterVariant: "text" } },
  {
    accessorKey: "category",
    header: "Category",
    meta: { filterVariant: "select", groupable: true },
  },
  {
    id: "tagCount",
    header: "Tags",
    accessorFn: (row) => (row.tags ?? []).filter((t) => t.trim()).length,
  },
  {
    accessorKey: "status",
    header: "Status",
    meta: { filterVariant: "select", groupable: true },
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  {
    accessorKey: "impressions",
    header: "Impressions",
    cell: ({ row }) => row.original.impressions.toLocaleString(),
  },
  {
    accessorKey: "publishedAt",
    header: "Published",
    cell: ({ row }) =>
      row.original.publishedAt ? new Date(row.original.publishedAt).toLocaleDateString() : "",
  },
  {
    accessorKey: "updatedAt",
    header: "Updated",
    cell: ({ row }) => (row.original.updatedAt ? new Date(row.original.updatedAt).toLocaleDateString() : ""),
  },
  {
    ...tableActionsColumn<InsightRow>(),
    cell: ({ row }) => (
      <TableRowActions
        edit={{ to: "/admin/insights/$id", params: { id: row.original.id } }}
        onDelete={() => onDelete(row.original)}
        deleteLabel={row.original.title}
      />
    ),
  },
];
}

function AdminInsightsList() {
  const data = Route.useLoaderData();
  const router = useRouter();

  async function handleDelete(row: InsightRow) {
    try {
      await deleteInsight({ data: { id: row.id } });
      toast.success("Insight deleted");
      router.invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete");
      throw e;
    }
  }

  const columns = buildColumns(handleDelete);

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Insights"
        action={
          <Button asChild>
            <Link to="/admin/insights/new">
              <Plus className="mr-2 h-4 w-4" /> New insight
            </Link>
          </Button>
        }
      />
      <InsightsAdminNav />
      <DataTable tableId="insights" columns={columns} data={data} />
    </AdminPageShell>
  );
}
